import React from 'react'

// Elements
import ParseContent from 'components/shared/ParseContent'

// Components
import styled from 'styled-components'

import ButtonDefault from 'components/elements/ButtonDefault'
import SubTitle from 'components/elements/SubTitle'
import { ScrollRevealItem } from 'components/shared/ScrollReveal'
import Plaatjie from '@ubo/plaatjie'

const ContentWrapper = styled.div<{ imageLeft?: boolean }>`
  position: relative;
  background-color: #ded2c7;
  ${({ imageLeft }) =>
    imageLeft
      ? `
    border-top-right-radius: 75px;
    border-top-left-radius: 0;
  `
      : `
    border-top-left-radius: 75px;
    border-top-right-radius: 0;
  `}

  @media (min-width: 992px) {
    min-height: 500px;
  }

  @media (max-width: 991px) {
    ${({ imageLeft }) =>
      imageLeft
        ? `
  border-top-right-radius: 0;
  border-bottom-right-radius: 50px;
`
        : `
  border-top-left-radius: 0;
  border-bottom-left-radius: 50px;
`}
  }
`

const Title = styled.h2`
  font-family: ${(props) => props.theme.font.family.secondary};
  font-size: ${(props) => props.theme.font.size['30']};
  line-height: 30px;
  margin-bottom: 24px;
  font-weight: 800;
  color: ${(props) => props.theme.color.text.secondary};
  text-transform: uppercase;

  @media (max-width: 991px) {
    font-size: ${(props) => props.theme.font.size['24']};
  }
`

const Content = styled(ParseContent)`
  & p {
    font-size: ${(props) => props.theme.font.size.base};
    font-weight: 300;
    color: ${(props) => props.theme.color.text.main};
    line-height: 30px;
  }
`

const StyledImage = styled(Plaatjie)`
  height: auto;

  @media (max-width: 991px) {
    height: 350px;

    & img {
      object-position: top;
    }
  }
`

const StyledVideo = styled.video`
  width: 100%;
  height: 100%;
  border: 0;
  object-fit: cover;
  min-height: 350px;

  @media (min-width: 992px) {
    height: 100%;
    min-height: 500px;
  }
`

const Rectangle = styled.div<{ $imageLeft?: boolean }>`
  background-color: ${(props) => props.theme.color.face.secondary};
  width: 157px;
  height: 15px;
  transform: rotate(90deg);
  position: absolute;
  z-index: 1;
  bottom: 4.5rem;
  ${({ $imageLeft }) =>
    $imageLeft
      ? `
    left: -4.85rem;
    right: auto;
  `
      : `
    right: -4.85rem;
    left: auto;
  `}
`

interface TextWithImageProps {
  // eslint-disable-next-line
  fields: GatsbyTypes.WpPage_Flexcontent_Flex_Text
}

function descriptionHasStrongTag(description: unknown): boolean {
  return typeof description === 'string' && /<\s*strong\b/i.test(description)
}

function hasEmbedUrl(url: unknown): url is string {
  return typeof url === 'string' && url.trim().length > 0
}

function TextWithImage({ fields }: TextWithImageProps) {
  const showStrongUnderline = Boolean(
    !fields?.showBalk && descriptionHasStrongTag(fields?.description)
  )
  const hasVideo = hasEmbedUrl(fields?.videoUrl)
  const hasImage = Boolean(fields?.image)

  return (
    <section id={fields?.anchorId || undefined}>
      <div className="container py-5">
        <div className="row no-gutters py-lg-5">
          <ContentWrapper
            imageLeft={Boolean(fields?.imageLeft)}
            className={`${
              fields?.liggendeAfbeelding ? 'col-lg-6' : 'col-lg-8'
            } py-5 px-4 px-lg-5 d-flex flex-column justify-content-between ${
              fields?.imageLeft ? 'order-2' : 'order-1'
            }`}
          >
            <div className="px-lg-3 py-lg-5 mb-5 mb-lg-0">
              <Title>{fields?.title}</Title>
              <Content
                content={fields.description}
                strongUnderlineSvg={showStrongUnderline}
              />
            </div>
            <div className="px-lg-3">
              {fields?.subtitle && <SubTitle>{fields.subtitle}</SubTitle>}
              {fields?.links &&
                fields?.links?.map((link: any, linkIndex: number) => {
                  const item = link?.link
                  return (
                    <ScrollRevealItem
                      key={item.title}
                      className="d-inline-flex"
                      sequenceIndex={linkIndex}
                    >
                      <ButtonDefault
                        to={item.url || '/'}
                        className="d-inline-flex"
                      >
                        {item.title}
                      </ButtonDefault>
                    </ScrollRevealItem>
                  )
                })}
            </div>
            {fields?.showBalk && (
              <Rectangle $imageLeft={Boolean(fields?.imageLeft)} />
            )}
          </ContentWrapper>
          {(hasVideo || hasImage) && (
            <div
              className={`${
                fields?.liggendeAfbeelding ? 'col-lg-6' : 'col-lg-4'
              } d-flex mt-0 position-relative ${
                fields?.imageLeft ? 'order-1' : '-order-1 order-lg-2'
              }`}
            >
              {hasVideo ? (
                <StyledVideo
                  src={fields?.videoUrl?.trim()}
                  title={fields?.title || 'Embedded video'}
                  autoPlay
                  muted
                  playsInline
                  loop
                  preload="metadata"
                  className="position-lg-absolute"
                  style={{ inset: 0 }}
                />
              ) : (
                <StyledImage
                  image={fields?.image}
                  alt="Lofthus"
                  loading="lazy"
                  className="position-lg-absolute"
                  style={{ inset: 0 }}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

export default TextWithImage
